import {
  Box,
  Button,
  Flex,
  FormLabel,
  Heading,
  Icon,
  ScaleFade,
  Text,
  VStack,
  SimpleGrid,
  Input,
  InputGroup,
  InputLeftElement,
  useColorModeValue,
} from "@chakra-ui/react";
import { Field, Form, Formik } from "formik";
import React, { useEffect, useState, useMemo } from "react";
import { BiLowVision, BiShow } from "react-icons/bi";
import { SearchIcon, ArrowLeftIcon } from "@chakra-ui/icons";
import { FaBookOpen } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { getAllSubjectsRequest } from "../../modules/admin-modules/subjects/Actions";
import { loginUserAction } from "../../modules/auth/Actions";
import PublicLayout from "../public/Layout/layout";

const LoginForm = (props) => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.auth.isLoading);
  const [inputType, setInputType] = useState("password");
  const subjects = useSelector((state) => state.subjects.subjects);
  const [currentStep, setCurrentStep] = useState(1); // 1: Subject selection, 2: Login form
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    dispatch(getAllSubjectsRequest());
  }, []);

  const palette = {
    primary: "#7A1422",
    secondary: "#8B1828",
    tertiary: "#D6CEC0",
    neutral: "#F3ECE6",
    ink: "#2D2A26",
    soft: "#6B5A5A",
  };

  // Filter subjects based on search
  const filteredSubjects = useMemo(() => {
    const safeSubjects = Array.isArray(subjects) ? subjects : [];
    return safeSubjects.filter((subject) => {
      const matchesSearch = subject.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [subjects, searchTerm]);

  const validateUserName = (value) => {
    let error;
    if (!value) {
      error = "Required";
    }
    return error;
  };

  const validatePassword = (value) => {
    let error;
    if (!value) {
      error = "Required";
    }
    return error;
  };

  const handleSubjectSelect = (subject) => {
    setSelectedSubject(subject);
    setCurrentStep(2);
  };

  const handleBackToSubjects = () => {
    setCurrentStep(1);
    setSelectedSubject(null);
  };

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const baseUrl = "https://api.edutopiaonline.com/";

  // Step 1: Subject Selection
  const renderSubjectSelection = () => (
    <VStack spacing={8} align="stretch" w="100%">
      <VStack spacing={4} align="center">
        <Heading 
          textAlign="center" 
          mb="6"
          fontSize={{ base: "28px", md: "36px" }}
          fontWeight="800"
          color="#8B1828"
          fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
        >
          Choose the subject
        </Heading>

      </VStack>
      
      {/* Search Bar */}
      <Box>
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color={palette.primary} />
          </InputLeftElement>
          <Input
            placeholder="Search for the subject..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="lg"
            borderRadius="xl"
            border="1px"
            borderColor={palette.tertiary}
            bg="rgba(255, 255, 255, 0.72)"
            backdropFilter="blur(12px)"
            _focus={{
              borderColor: palette.primary,
              boxShadow: "0 0 0 3px rgba(122, 20, 34, 0.12)",
            }}
            _hover={{
              borderColor: palette.primary,
            }}
            fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
          />
        </InputGroup>
      </Box>

      {/* Results Count */}
      <Text 
        fontSize="sm" 
        color={palette.soft}
        fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
      >
        Found {filteredSubjects.length} subjects
      </Text>

      {/* Subject List */}
      <Box>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {filteredSubjects.map((subject) => (
            <Button
              key={subject.id}
              onClick={() => handleSubjectSelect(subject)}
              variant="outline"
              height="auto"
              p={8}
              textAlign="left"
              borderRadius="xl"
              borderWidth="1px"
              borderColor={palette.tertiary}
              bg="rgba(255, 255, 255, 0.72)"
              backdropFilter="blur(12px)"
              _hover={{
                borderColor: palette.primary,
                bg: "rgba(255, 255, 255, 0.88)",
                shadow: "0 14px 30px rgba(74, 15, 31, 0.12)",
                transform: "translateY(-3px)",
              }}
              _active={{
                transform: 'scale(0.98)',
              }}
              transition="all 0.3s ease"
              boxShadow="0 4px 15px rgba(74, 15, 31, 0.08)"
            >
              <Flex justify="space-between" align="flex-start" width="100%">
                <VStack align="start" spacing={3} flex={1} minW={0}>
                  <Text 
                    fontSize="lg" 
                    fontWeight="700" 
                    wordBreak="break-word" 
                    width="100%" 
                    whiteSpace="normal" 
                    overflowWrap="break-word"
                    color={palette.secondary}
                    fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
                    lineHeight="1.4"
                  >
                    {subject.name}
                  </Text>
                  <Text 
                    fontSize="sm" 
                    color={palette.soft}
                    fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
                  >
                    Press to select
                  </Text>
                </VStack>

                {subject.logo ? (
                  <Box
                    width="90px"
                    height="90px"
                    borderRadius="xl"
                    overflow="hidden"
                    bg="rgba(243, 236, 230, 0.9)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    ml={4}
                    flexShrink={0}
                    border="1px solid"
                    borderColor={palette.tertiary}
                    boxShadow="0 4px 15px rgba(74, 15, 31, 0.08)"
                  >
                    <img
                      src={baseUrl + subject.logo}
                      alt={subject.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                ) : (
                  <Box
                    width="90px"
                    height="90px"
                    borderRadius="xl"
                    overflow="hidden"
                    bg="rgba(233, 226, 219, 0.9)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    ml={4}
                    flexShrink={0}
                    border="1px solid"
                    borderColor={palette.tertiary}
                    boxShadow="0 4px 15px rgba(74, 15, 31, 0.08)"
                  >
                    <img
                      src={"/logo.png"}
                      alt={subject.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                )}
              </Flex>
            </Button>
          ))}
        </SimpleGrid>

        {filteredSubjects.length === 0 && (
          <VStack py={16} spacing={6}>
            <Icon as={FaBookOpen} boxSize={16} color={palette.primary} />
            <Text 
              fontSize="xl" 
              fontWeight="700" 
              color={palette.secondary}
              fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
            >
              No subjects found
            </Text>
            <Text 
              color={palette.soft}
              textAlign="center"
              fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
            >
              Try modifying the search or filtering criteria
            </Text>
          </VStack>
        )}
      </Box>
    </VStack>
  );

  // Step 2: Login Form
  const renderLoginForm = () => (
    <Formik
      initialValues={{
        phone: "",
        password: "",
        subject_id: selectedSubject?.id || null,
      }}
      onSubmit={(values) => {
        let formData = new FormData();
        formData.append("phone", values.phone);
        formData.append("password", values.password);
        formData.append("subject_id", values.subject_id);

        dispatch(loginUserAction(formData));
      }}
    >
      {({ errors, touched, values, setFieldValue }) => {
        // Update form value when subject is selected
        if (selectedSubject && values.subject_id !== selectedSubject.id) {
          setFieldValue("subject_id", selectedSubject.id);
        }

        return (
          <Form>
            <VStack spacing={8} align="stretch" w="100%">
              {/* Back Button and Header */}
              <Flex justify="space-between" align="center">
                <Button
                  leftIcon={<ArrowLeftIcon />}
                  variant="ghost"
                  onClick={handleBackToSubjects}
                  color={palette.soft}
                  _hover={{ 
                    bg: palette.neutral,
                    color: palette.primary
                  }}
                  fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
                  fontWeight="600"
                >
                  Return to the subjects
                </Button>
                <Heading 
                  size="lg"
                  color={palette.secondary}
                  fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
                  fontWeight="800"
                >
                  Login
                </Heading>
                <Box w="40px" /> {/* Spacer for centering */}
              </Flex>

              {/* Selected Subject Display */}
              {selectedSubject && (
                <Box
                  p={6}
                  bg={palette.neutral}
                  border="1px solid"
                  borderColor={palette.tertiary}
                  borderRadius="xl"
                  boxShadow="0 4px 15px rgba(74, 15, 31, 0.08)"
                >
                  <Text 
                    fontSize="md" 
                    color={palette.primary}
                    fontWeight="700"
                    fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
                  >
                    Selected subject: {selectedSubject.name}
                  </Text>
                </Box>
              )}

              {/* Phone Input */}
              <Box>
                <FormLabel
                  htmlFor="phone"
                  color={palette.secondary}
                  fontSize="18px"
                  fontWeight="700"
                  mb={3}
                  fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
                >
                  Phone number
                </FormLabel>

                <Field
                  as={Input}
                  name="phone"
                  validate={validateUserName}
                  placeholder="Enter your phone number"
                  size="lg"
                  borderRadius="xl"
                  border="1px solid"
                  borderColor={palette.tertiary}
                  bg="rgba(255, 255, 255, 0.72)"
                  backdropFilter="blur(12px)"
                  _focus={{
                    borderColor: palette.primary,
                    boxShadow: "0 0 0 3px rgba(106, 30, 30, 0.12)",
                  }}
                  _hover={{
                    borderColor: palette.primary,
                  }}
                  fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
                />
                {errors.phone && touched.phone && (
                  <Text color="red.500" fontSize="sm" mt={2} fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif">
                    {errors.phone}
                  </Text>
                )}
              </Box>

              {/* Password Input */}
              <Box position="relative">
                <FormLabel
                  htmlFor="password"
                  color={palette.secondary}
                  fontSize="18px"
                  fontWeight="700"
                  mb={3}
                  fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
                >
                  Password
                </FormLabel>

                <Field
                  as={Input}
                  name="password"
                  validate={validatePassword}
                  type={inputType}
                  placeholder="Enter your password"
                  size="lg"
                  borderRadius="xl"
                  border="1px solid"
                  borderColor={palette.tertiary}
                  bg="rgba(255, 255, 255, 0.72)"
                  backdropFilter="blur(12px)"
                  pr="50px"
                  _focus={{
                    borderColor: palette.primary,
                    boxShadow: "0 0 0 3px rgba(106, 30, 30, 0.12)",
                  }}
                  _hover={{
                    borderColor: palette.primary,
                  }}
                  fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
                />

                <Icon
                  position="absolute"
                  right="15px"
                  top="50px"
                  fontSize="20px"
                  color={palette.soft}
                  cursor="pointer"
                  _hover={{ color: palette.primary }}
                  as={inputType == "password" ? BiShow : BiLowVision}
                  onClick={() =>
                    inputType == "password"
                      ? setInputType("text")
                      : setInputType("password")
                  }
                />
                {errors.password && touched.password && (
                  <Text color="red.500" fontSize="sm" mt={2} fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif">
                    {errors.password}
                  </Text>
                )}
              </Box>

              {/* Login Button */}
              <Button
                type="submit"
                w="full"
                p="20px"
                alignSelf="center"
                isLoading={isLoading}
                bg={palette.secondary}
                color={palette.neutral}
                mt="20px"
                borderRadius="xl"
                fontSize="18px"
                fontWeight="700"
                _hover={{
                  bg: palette.primary,
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 28px rgba(74, 15, 31, 0.25)"
                }}
                _active={{ transform: "translateY(0)" }}
                transition="all 0.3s ease"
                boxShadow="0 6px 18px rgba(74, 15, 31, 0.16)"
                fontFamily="'Tajawal', 'Amiri', 'Scheherazade New', 'Noto Sans Arabic', 'Cairo', sans-serif"
              >
                Login
              </Button>
            </VStack>
          </Form>
        );
      }}
    </Formik>
  );

  return (
    <PublicLayout color="main_1" bg="white" register={false}>
      <Flex
        flexDirection="column"
        alignItems="center"
        my="50px"
        w="100%"
        h="100%"
      >
        <Box
          sx={{ 
            width: { base: "95%", md: currentStep === 1 ? "90%" : "40%" },
            maxWidth: currentStep === 1 ? "1200px" : "500px"
          }}
          transition="all 260ms ease"
        >
          <ScaleFade initialScale={0.98} in={currentStep === 1} unmountOnExit>
            <Box>{renderSubjectSelection()}</Box>
          </ScaleFade>

          <ScaleFade initialScale={0.98} in={currentStep === 2} unmountOnExit>
            <Box>{renderLoginForm()}</Box>
          </ScaleFade>
        </Box>
      </Flex>
    </PublicLayout>
  );
};

export default LoginForm;
